import { useMemo } from 'react';

import {
  companyIdState,
  type EnrichedFromCrunchbaseCompanyData,
} from '../overview/company.state';
import { useCompanyTags } from '../overview/tags/use-company-tags.query';
import { useCompanyEnrichedWithCrunchbase } from '../overview/use-company-enriched-with-crunchbase';
import { useCompanyInvestors } from '../overview/investors/use-company-investors.query';
import { hasProperty } from '@/helpers/has-property';
import { useAtomValue } from 'jotai';

type EnrichedFromCbCompanyPageElement =
  | 'header'
  | 'about'
  | 'investors'
  | 'tags'
  | 'funding';

type OmitFromUnion<T, K> = T extends K ? never : T;

type CompanyFieldsEnrichedFromCrunchbase = Record<
  OmitFromUnion<EnrichedFromCbCompanyPageElement, 'investors'>,
  (keyof EnrichedFromCrunchbaseCompanyData)[]
>;

export const useCrunchbaseEnrichmentCheck = (
  companyPageElement: EnrichedFromCbCompanyPageElement,
) => {
  const enrichedFromCbData = useCompanyEnrichedWithCrunchbase();
  const companyId = useAtomValue(companyIdState);
  const { data: companyInvestors } = useCompanyInvestors(companyId);
  const { data: companyTags } = useCompanyTags(companyId);

  const companyFieldsEnrichedFromCrunchbase: CompanyFieldsEnrichedFromCrunchbase =
    {
      header: [
        'name',
        'activityStatus',
        'logoUrl',
        'linkedinUrl',
        'website',
        'ownershipStatus',
        'stockExchangeSymbol',
        'ticker',
        'yearEstablished',
        'employeesRange',
        'keyOffering',
        'ownedByIrm',
        'hqLocation',
      ],
      about: ['detailedDescription'],
      tags: ['tags'],
      funding: ['capitalRaised', 'fundingStage', 'lastFundingDate'],
    };

  const isEnrichedFromCrunchbase = useMemo(() => {
    if (!enrichedFromCbData) {
      return false;
    }

    if (companyPageElement === 'investors') {
      return companyInvestors?.poweredBy === 'crunchbase';
    }

    if (companyPageElement === 'tags') {
      return companyTags?.poweredBy === 'crunchbase';
    }

    const areAllFieldsEnriched = companyFieldsEnrichedFromCrunchbase[
      companyPageElement
    ]
      .filter(field => !!enrichedFromCbData?.[field])
      .every(field => enrichedFromCbData?.[field]?.poweredBy === 'crunchbase');

    return areAllFieldsEnriched;
  }, [
    companyFieldsEnrichedFromCrunchbase,
    companyInvestors?.poweredBy,
    companyPageElement,
    companyTags?.poweredBy,
    enrichedFromCbData,
  ]);

  const lastEditedAt = useMemo(() => {
    if (companyPageElement === 'investors') {
      return companyInvestors?.lastEditedAt;
    }

    if (companyPageElement === 'tags') {
      return companyTags?.lastEditedAt;
    }

    const relevantFields =
      companyFieldsEnrichedFromCrunchbase[companyPageElement];
    const timestamps = relevantFields
      .map(field => {
        const value = enrichedFromCbData?.[field];
        return hasProperty('lastEditedAt', value) ? value.lastEditedAt : null;
      })
      .filter((date): date is string => !!date);

    if (timestamps.length === 0) return null;

    const latestDate = new Date(
      Math.max(...timestamps.map(date => new Date(date).getTime())),
    );
    return latestDate.toISOString();
  }, [
    companyPageElement,
    companyInvestors?.lastEditedAt,
    companyTags?.lastEditedAt,
    enrichedFromCbData,
    companyFieldsEnrichedFromCrunchbase,
  ]);

  return {
    isEnrichedFromCrunchbase,
    lastEditedAt,
  };
};
